import React, { useEffect, useMemo, useRef, useState } from "react";
import { normalizeCompany, suggestCompany } from "./schema";

// Multi-select adresatow "w stylu CompanyCell": wpisujesz tekst, dostajesz liste
// dopasowan ze slownika, podpowiedz "czy chodzilo o...", mozliwosc dodania nowego
// adresata do bazy. Wybrani adresaci sa chipami; wartosc na zewnatrz to string
// rozdzielony przecinkami (backend dzieli po ,/;/spacji/nowej linii).
//
// props:
//   value           - string "a@x.com, b@y.com"
//   options         - string[] znanych adresatow ze slownika
//   dict            - Map(znormalizowany klucz -> pisownia kanoniczna)
//   hasError        - czy pokazac stan bledu
//   errorMessage    - tresc bledu (tooltip)
//   onChange(v)     - nowa wartosc (string rozdzielony przecinkami)
//   onAddOption(name) => Promise<string>  - dodaje adresata do bazy, zwraca kanoniczna nazwe

const MAX_MATCHES = 8;

const wrapStyle = { position: "relative", width: "100%" };

const chipsRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  marginBottom: 6,
};

const chipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "#e3f2fd",
  border: "1px solid #90caf9",
  color: "#0d47a1",
  borderRadius: 14,
  padding: "3px 6px 3px 10px",
  fontSize: 13,
  maxWidth: "100%",
};

const chipRemoveStyle = {
  border: "none",
  background: "#bbdefb",
  color: "#0d47a1",
  borderRadius: "50%",
  width: 18,
  height: 18,
  lineHeight: "16px",
  cursor: "pointer",
  fontSize: 13,
  padding: 0,
};

const menuStyle = {
  position: "fixed",
  zIndex: 2000,
  background: "#fff",
  border: "1px solid #bbb",
  borderRadius: 4,
  boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
  maxHeight: 320,
  overflowY: "auto",
  fontSize: 15,
};

const itemStyle = {
  padding: "9px 12px",
  cursor: "pointer",
  borderBottom: "1px solid #f0f0f0",
  whiteSpace: "nowrap",
};

const actionBtn = {
  border: "1px solid #1976d2",
  color: "#1976d2",
  background: "#fff",
  borderRadius: 4,
  padding: "3px 10px",
  cursor: "pointer",
  fontSize: 13,
};

const knownChipStyle = {
  cursor: "pointer",
  border: "1px solid #1976d2",
  color: "#1976d2",
  background: "#fff",
  borderRadius: 4,
  padding: "2px 8px",
  fontSize: 13,
};

export const parseRecipients = (value) => {
  const out = [];
  const seen = new Set();
  String(value || "")
    .split(/[;,\n]+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .forEach((item) => {
      const key = normalizeCompany(item);
      if (!seen.has(key)) {
        seen.add(key);
        out.push(item);
      }
    });
  return out;
};

const joinRecipients = (list) => list.join(", ");

const RecipientsCell = ({
  value,
  options = [],
  dict,
  hasError,
  errorMessage,
  onChange,
  onAddOption,
}) => {
  const inputRef = useRef(null);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [hi, setHi] = useState(-1);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");

  const selected = useMemo(() => parseRecipients(value), [value]);
  const selectedKeys = useMemo(
    () => new Set(selected.map((s) => normalizeCompany(s))),
    [selected],
  );

  const q = normalizeCompany(text);

  const matches = useMemo(() => {
    if (!q) return [];
    const out = [];
    for (const opt of options) {
      const key = normalizeCompany(opt);
      if (key.includes(q) && !selectedKeys.has(key)) {
        out.push(opt);
        if (out.length >= MAX_MATCHES) break;
      }
    }
    return out;
  }, [q, options, selectedKeys]);

  const exact = Boolean(q) && dict && dict.has(q);
  const suggestion = useMemo(
    () => (!exact && q ? suggestCompany(text, dict) : null),
    [exact, q, text, dict],
  );
  const showSuggestion =
    suggestion &&
    !selectedKeys.has(normalizeCompany(suggestion)) &&
    !matches.some((m) => normalizeCompany(m) === normalizeCompany(suggestion));
  const canAdd =
    Boolean(q) &&
    !selectedKeys.has(q) &&
    typeof onAddOption === "function";

  const knownUnselected = useMemo(
    () => options.filter((opt) => !selectedKeys.has(normalizeCompany(opt))),
    [options, selectedKeys],
  );

  const updateRect = () => {
    if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
  };

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const openMenu = () => {
    updateRect();
    setHi(-1);
    setAddError("");
    setOpen(true);
  };

  const canonicalOf = (name) => {
    const key = normalizeCompany(name);
    return dict && dict.has(key) ? dict.get(key) : String(name || "").trim();
  };

  // keepFocus = true dla dodania z pola/menu (kontynuujemy wpisywanie),
  // false dla klikniecia w "znani adresaci" (tylko przesuwamy chip, bez fokusa i menu).
  const addRecipient = (name, { keepFocus = true } = {}) => {
    const clean = canonicalOf(name);
    if (!clean) return;
    if (!selectedKeys.has(normalizeCompany(clean))) {
      onChange(joinRecipients([...selected, clean]));
    }
    setText("");
    setHi(-1);
    if (keepFocus) {
      if (inputRef.current) inputRef.current.focus();
    } else {
      setOpen(false);
    }
  };

  const removeRecipient = (name) => {
    const key = normalizeCompany(name);
    onChange(
      joinRecipients(selected.filter((s) => normalizeCompany(s) !== key)),
    );
  };

  const handleAdd = async () => {
    const name = text.trim();
    if (!name) return;
    if (dict && dict.has(normalizeCompany(name))) {
      addRecipient(dict.get(normalizeCompany(name)));
      return;
    }
    setAdding(true);
    setAddError("");
    try {
      const canonical = await onAddOption(name);
      addRecipient(canonical || name);
    } catch (e) {
      setAddError("Nie udało się dodać adresata do bazy");
    } finally {
      setAdding(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) openMenu();
      setHi((h) => Math.min(h + 1, matches.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((h) => Math.max(h - 1, -1));
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "Backspace" && text === "" && selected.length > 0) {
      removeRecipient(selected[selected.length - 1]);
      return;
    }
    if (e.key === "Enter" || e.key === ",") {
      if (open && hi >= 0 && matches[hi]) {
        e.preventDefault();
        addRecipient(matches[hi]);
        return;
      }
      if (text.trim()) {
        e.preventDefault();
        if (exact) addRecipient(dict.get(q));
        else if (canAdd) handleAdd();
        else addRecipient(text.trim());
      }
    }
  };

  const inputStyle = {
    border: `1px solid ${hasError ? "#d32f2f" : "#ccc"}`,
    borderRadius: 4,
    padding: "10px 12px",
    font: "inherit",
    fontSize: 16,
    width: "100%",
    boxSizing: "border-box",
    background: hasError ? "#fff5f5" : "#fff",
  };

  return (
    <div style={wrapStyle}>
      {selected.length > 0 && (
        <div style={chipsRowStyle}>
          {selected.map((r) => (
            <span key={r} style={chipStyle}>
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {r}
              </span>
              <button
                type="button"
                style={chipRemoveStyle}
                aria-label={`Usuń ${r}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => removeRecipient(r)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <input
        ref={inputRef}
        type="text"
        autoComplete="off"
        value={text}
        placeholder="Dodaj adresata…"
        title={hasError ? errorMessage : undefined}
        style={inputStyle}
        onChange={(e) => {
          setText(e.target.value);
          if (!open) openMenu();
          else updateRect();
          setHi(-1);
        }}
        onFocus={openMenu}
        onBlur={() => setOpen(false)}
        onKeyDown={handleKeyDown}
      />

      {open && rect && (
        <div
          style={{
            ...menuStyle,
            top: rect.bottom + 2,
            left: rect.left,
            width: Math.max(rect.width, 260),
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {matches.map((m, i) => (
            <div
              key={`${m}-${i}`}
              style={{ ...itemStyle, background: i === hi ? "#e3f2fd" : "#fff" }}
              onMouseEnter={() => setHi(i)}
              onClick={() => addRecipient(m)}
            >
              {m}
            </div>
          ))}

          {!q && (
            <div style={{ ...itemStyle, color: "#888", cursor: "default" }}>
              Zacznij pisać adresata…
            </div>
          )}

          {q && !exact && matches.length === 0 && (
            <div style={{ ...itemStyle, color: "#888", cursor: "default" }}>
              Brak dopasowań w słowniku
            </div>
          )}

          {(showSuggestion || canAdd || exact) && (
            <div
              style={{
                padding: "8px 10px",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 8,
                background: "#fafafa",
              }}
            >
              {exact && (
                <button
                  type="button"
                  style={actionBtn}
                  onClick={() => addRecipient(dict.get(q))}
                >
                  Dodaj „{dict.get(q)}”
                </button>
              )}
              {showSuggestion && (
                <>
                  <span style={{ color: "#555" }}>
                    Czy chodziło o: <strong>{suggestion}</strong>?
                  </span>
                  <button
                    type="button"
                    style={actionBtn}
                    onClick={() => addRecipient(suggestion)}
                  >
                    Użyj
                  </button>
                </>
              )}
              {canAdd && !exact && (
                <button
                  type="button"
                  style={{
                    ...actionBtn,
                    borderColor: "#2e7d32",
                    color: "#2e7d32",
                  }}
                  disabled={adding}
                  onClick={handleAdd}
                >
                  {adding
                    ? "Dodawanie…"
                    : `+ Dodaj „${text.trim()}” do bazy`}
                </button>
              )}
              {addError && (
                <span style={{ color: "#d32f2f", width: "100%" }}>
                  {addError}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {knownUnselected.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
            Znani adresaci (kliknij, aby dodać):
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {knownUnselected.map((opt) => (
              <span
                key={opt}
                style={knownChipStyle}
                onClick={() => addRecipient(opt, { keepFocus: false })}
              >
                {opt}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipientsCell;
